import * as React from "react";
import { Link } from "react-router";

import { FactList } from "@/shared/components/FactList";
import {
  INPUT_SOURCE_COPY,
  triggeringDatasetCopy,
  type TriggeringSubject,
} from "@/shared/components/invocationSpec/inputSourceCopy";
import { invocationSpecValuesFromWire } from "@/shared/components/invocationSpec/invocationSpecFromWire";
import { type InputSourceValues } from "@/shared/components/invocationSpec/invocationSpecSchema";
import { DatasetLink } from "@/shared/components/links";
import {
  formatCompute,
  stringifyCommand,
  type DataSelector,
} from "@/shared/domain/actions";
import {
  TRIGGERING_SESSION_SELECTOR,
  type ActionInvocationSpec,
} from "@/shared/domain/triggers";

import styles from "./InvocationSpecFacts.module.css";

export interface InvocationSpecFactsProps {
  spec: ActionInvocationSpec;
  /** What the invocation is always handed, listed before the stored inputs. */
  pinnedInput?: React.ReactNode;
  /** What the firing is about, for wording a triggering-dataset input. */
  triggeringSubject?: TriggeringSubject;
  /** Where output goes when the spec names no destination. */
  defaultOutput: string;
}

/** Path to an action's page, owner-qualified when the spec pins one. */
function actionPath(name: string, ownerId?: string | null): string {
  const parts = ["/actions"];
  if (ownerId) {
    parts.push(encodeURIComponent(ownerId));
  }
  parts.push(encodeURIComponent(name));
  return parts.join("/");
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className={styles.muted}>{children}</span>;
}

function formatValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

function globsPhrase(globs: readonly string[]): string {
  return globs.length === 0 ? "every file" : globs.join(", ");
}

function InputRow({
  row,
  triggeringSubject,
}: {
  row: InputSourceValues;
  triggeringSubject?: TriggeringSubject;
}) {
  switch (row.source) {
    case "triggering-dataset": {
      const copy =
        triggeringSubject === undefined
          ? INPUT_SOURCE_COPY["triggering-dataset"]
          : triggeringDatasetCopy(triggeringSubject);
      return (
        <>
          {copy.title}:{" "}
          <span className={styles.mono}>{globsPhrase(row.globs)}</span>
        </>
      );
    }
    case "dataset":
      return (
        <>
          {INPUT_SOURCE_COPY.dataset.title}{" "}
          {row.datasetId === null ? (
            <Muted>(none picked)</Muted>
          ) : (
            <DatasetLink datasetId={row.datasetId} />
          )}
          : <span className={styles.mono}>{globsPhrase(row.globs)}</span>
        </>
      );
    case "query":
      return (
        <>
          {INPUT_SOURCE_COPY.query.title}:{" "}
          <span className={styles.mono}>{row.text}</span>
        </>
      );
  }
}

/** One way a topic or session selector picks inputs, as a label and its value. */
interface SelectorLine {
  label: string;
  value?: string;
}

function isTriggeringSession(selector: DataSelector): boolean {
  return (
    selector.query === null &&
    selector.names === null &&
    selector.ids?.length === 1 &&
    selector.ids[0].replace(/\s/g, "") === TRIGGERING_SESSION_SELECTOR.ids?.[0]
  );
}

/** A stored topic or session selector as lines, one per field it fills. */
function selectorLines(
  noun: "Topics" | "Sessions",
  selectors: DataSelector | DataSelector[] | null | undefined,
): SelectorLine[] {
  if (selectors === null || selectors === undefined) {
    return [];
  }
  return [selectors].flat().flatMap((selector) => {
    if (noun === "Sessions" && isTriggeringSession(selector)) {
      return [{ label: "The session that fired" }];
    }
    const lines: SelectorLine[] = [];
    if (selector.query) {
      lines.push({ label: `${noun} matching a query`, value: selector.query });
    }
    if (selector.ids && selector.ids.length > 0) {
      lines.push({ label: `${noun} by ID`, value: selector.ids.join(", ") });
    }
    if (selector.names && selector.names.length > 0) {
      lines.push({ label: `${noun} named`, value: selector.names.join(", ") });
    }
    return lines;
  });
}

function containerLines(
  container: NonNullable<ActionInvocationSpec["container_parameter_overrides"]>,
): string[] {
  const lines: string[] = [];
  if (container.entry_point) {
    lines.push(`Entry point: ${stringifyCommand(container.entry_point)}`);
  }
  if (container.command) {
    lines.push(`Command: ${stringifyCommand(container.command)}`);
  }
  if (container.workdir) {
    lines.push(`Working directory: ${container.workdir}`);
  }
  const envNames = Object.keys(container.env_vars ?? {});
  if (envNames.length > 0) {
    lines.push(`Environment: ${envNames.join(", ")}`);
  }
  return lines;
}

/**
 * An invocation spec, read: the facts a trigger's invoke-action target and an
 * ingestion rule share, as items for the caller's own `FactList`.
 * Each setting left to the action says so rather than disappearing.
 */
export function InvocationSpecFacts({
  spec,
  pinnedInput,
  triggeringSubject,
  defaultOutput,
}: InvocationSpecFactsProps) {
  const parameters = Object.entries(spec.parameter_values);
  const { inputs, topicAndSessionSelectors } =
    invocationSpecValuesFromWire(spec);
  const selected = [
    ...selectorLines("Topics", topicAndSessionSelectors?.topics),
    ...selectorLines("Sessions", topicAndSessionSelectors?.sessions),
  ];
  const compute = formatCompute(spec.compute_requirement_overrides ?? {});
  const container =
    spec.container_parameter_overrides === null ||
    spec.container_parameter_overrides === undefined
      ? []
      : containerLines(spec.container_parameter_overrides);
  const destination = spec.upload_destination ?? null;

  return (
    <>
      <FactList.Item label="Action">
        <Link
          to={actionPath(spec.action.name, spec.action.owner)}
          className={styles.mono}
          data-testid="invocation-action"
        >
          {spec.action.name}
        </Link>
      </FactList.Item>
      {spec.action.digest !== null && spec.action.digest !== undefined && (
        <>
          <FactList.Item label="Pinned digest" mono>
            {spec.action.digest}
          </FactList.Item>
        </>
      )}

      <FactList.Item label="Parameters" data-testid="invocation-parameters">
        {parameters.length === 0 ? (
          <Muted>Action defaults</Muted>
        ) : (
          <ul className={styles.lines}>
            {parameters.map(([name, value]) => (
              <li key={name} className={styles.mono}>
                {name} = {formatValue(value)}
              </li>
            ))}
          </ul>
        )}
      </FactList.Item>

      <FactList.Item label="Input data" data-testid="invocation-inputs">
        {pinnedInput === undefined &&
        inputs.length === 0 &&
        selected.length === 0 ? (
          <Muted>None</Muted>
        ) : (
          <ul className={styles.lines}>
            {pinnedInput !== undefined && <li>{pinnedInput}</li>}
            {inputs.map((row, index) => (
              <li key={index}>
                <InputRow row={row} triggeringSubject={triggeringSubject} />
              </li>
            ))}
            {selected.map((line, index) => (
              <li key={`selected-${index}`}>
                {line.value === undefined ? (
                  line.label
                ) : (
                  <>
                    {line.label}:{" "}
                    <span className={styles.mono}>{line.value}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </FactList.Item>

      <FactList.Item label="Output">
        {destination === null || destination.destination_id === null ? (
          defaultOutput
        ) : (
          <DatasetLink datasetId={destination.destination_id} />
        )}
      </FactList.Item>

      <FactList.Item label="Compute" data-testid="invocation-compute">
        {compute === "" ? <Muted>Action defaults</Muted> : compute}
      </FactList.Item>

      <FactList.Item label="Container">
        {container.length === 0 ? (
          <Muted>Action defaults</Muted>
        ) : (
          <ul className={styles.lines}>
            {container.map((line) => (
              <li key={line} className={styles.mono}>
                {line}
              </li>
            ))}
          </ul>
        )}
      </FactList.Item>

      <FactList.Item label="Timeout">
        {spec.timeout === null || spec.timeout === undefined ? (
          <Muted>Action default</Muted>
        ) : (
          `${spec.timeout} minutes`
        )}
      </FactList.Item>
    </>
  );
}
